import { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";

const formatTime = (sec) => {
  if (!isFinite(sec) || sec < 0) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

export const AudioPlayer = ({ src, title, testId = "audio-player" }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setReady(false);
  }, [src]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) audio.play();
    else audio.pause();
  };

  const onSeek = (e) => {
    const audio = audioRef.current;
    const t = Number(e.target.value);
    if (audio) audio.currentTime = t;
    setCurrentTime(t);
  };

  const onVolume = (e) => {
    const v = Number(e.target.value);
    setVolume(v);
    if (audioRef.current) audioRef.current.volume = v;
  };

  return (
    <div className="rounded-xl border border-cts-line bg-white p-5" data-testid={testId}>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        data-testid={`${testId}-element`}
        onLoadedMetadata={(e) => {
          setDuration(e.currentTarget.duration || 0);
          setReady(true);
        }}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={togglePlay}
          disabled={!ready}
          aria-label={playing ? `Pause ${title}` : `Play ${title}`}
          data-testid={`${testId}-playpause-btn`}
          className="shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-full bg-cts-red text-white disabled:opacity-50 hover:bg-cts-ink transition-colors"
        >
          {playing ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
        </button>
        <div className="flex-1 min-w-0">
          <input
            type="range"
            min={0}
            max={duration || 0}
            step={0.1}
            value={currentTime}
            onChange={onSeek}
            aria-label="Seek"
            data-testid={`${testId}-seek`}
            className="w-full accent-cts-red cursor-pointer"
          />
          <div className="flex justify-between text-xs text-cts-charcoal/70 font-mono mt-1">
            <span data-testid={`${testId}-current-time`}>{formatTime(currentTime)}</span>
            <span data-testid={`${testId}-duration`}>{formatTime(duration)}</span>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2 shrink-0">
          {volume === 0 ? (
            <VolumeX size={17} className="text-cts-charcoal/60" aria-hidden="true" />
          ) : (
            <Volume2 size={17} className="text-cts-charcoal/60" aria-hidden="true" />
          )}
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={volume}
            onChange={onVolume}
            aria-label="Volume"
            data-testid={`${testId}-volume`}
            className="w-20 accent-cts-red cursor-pointer"
          />
        </div>
      </div>
    </div>
  );
};
